/**
 * Estimate Cell Renderer
 *
 * Generates HTML for estimate display and inline editing
 */

import type { Lot, State } from '../../types';
import {
  formatCurrencyDisplay,
  formatEstimateInputValue,
  escapeHtml,
  escapeAttr,
} from '../formatting';
import { hasPricingData } from '../rendering';
import { getCachedEstimate, setCachedEstimate } from '../estimates';
import { formatPriceStatusValue } from '../utils';
import { estimateSkeleton } from './helpers';
import styles from './tableCells.module.css';

export type EstimateEditState = { id: string; low?: string; high?: string };

export function buildEstimateCell(lot: Lot, state: State): string {
  const l = lot;

  let lowEstimate = '-';
  if (l.lowSellValue != null) {
    const lowAmount = Number(l.lowSellValue);
    const formattedLow = formatCurrencyDisplay(lowAmount);
    lowEstimate = formattedLow != null ? formattedLow : '$' + String(l.lowSellValue);
  } else if (l.lowSellValueRaw != null) {
    lowEstimate = escapeHtml(String(l.lowSellValueRaw));
  }

  let highEstimate = '-';
  if (l.highSellValue != null) {
    const highAmount = Number(l.highSellValue);
    const formattedHigh = formatCurrencyDisplay(highAmount);
    highEstimate = formattedHigh != null ? formattedHigh : '$' + String(l.highSellValue);
  } else if (l.highSellValueRaw != null) {
    highEstimate = escapeHtml(String(l.highSellValueRaw));
  }

  // --- Estimate field with skeleton, cache ---
  let lowHighEstimate;
  const hasAnyPricing = hasPricingData(l);
  const cached = getCachedEstimate(l.id);
  if (!hasAnyPricing && cached) {
    lowHighEstimate = cached;
  } else if (!hasAnyPricing) {
    lowHighEstimate = estimateSkeleton();
  } else {
    const hasLowEstimate = lowEstimate !== '-';
    const hasHighEstimate = highEstimate !== '-';
    if (hasLowEstimate || hasHighEstimate) {
      const lowDisplay = hasLowEstimate ? lowEstimate : '—';
      const highDisplay = hasHighEstimate ? highEstimate : '—';
      const lowHtml = hasLowEstimate ? '<strong>' + lowDisplay + '</strong>' : lowDisplay;
      lowHighEstimate = lowHtml + ' - ' + highDisplay;
      setCachedEstimate(l.id, lowHighEstimate);
    } else {
      let comparableCount = 0;
      if (Array.isArray(l.lowListings)) {
        comparableCount += l.lowListings.length;
      }
      if (Array.isArray(l.highListings)) {
        comparableCount += l.highListings.length;
      }
      if (comparableCount > 0) {
        const comparableLabel =
          comparableCount === 1 ? '1 comparable found' : comparableCount + ' comparables found';
        lowHighEstimate = escapeHtml(comparableLabel);
        setCachedEstimate(l.id, lowHighEstimate);
      } else if (typeof l.priceStatus === 'string' && l.priceStatus.trim()) {
        lowHighEstimate = escapeHtml(formatPriceStatusValue(l));
        setCachedEstimate(l.id, lowHighEstimate);
      } else if (l.aiAnalysis) {
        lowHighEstimate = escapeHtml('AI analysis available');
        setCachedEstimate(l.id, lowHighEstimate);
      } else {
        lowHighEstimate = escapeHtml('AI pricing available');
        setCachedEstimate(l.id, lowHighEstimate);
      }
    }
  }

  const canEditEstimate = !!l.id;
  const isEditingEstimate = Boolean(
    canEditEstimate && state.estimateEdit && state.estimateEdit.id === l.id
  );
  const pendingEstimateSave = Boolean(
    canEditEstimate && state.pendingEstimateIds && state.pendingEstimateIds.has(l.id)
  );

  let estimateCellContent;
  if (isEditingEstimate) {
    const currentEditState: EstimateEditState = state.estimateEdit || {
      id: '',
      low: undefined,
      high: undefined,
    };
    const editLowValue =
      typeof currentEditState.low === 'string'
        ? currentEditState.low
        : formatEstimateInputValue(l.lowSellValue, l.lowSellValueRaw);
    const editHighValue =
      typeof currentEditState.high === 'string'
        ? currentEditState.high
        : formatEstimateInputValue(l.highSellValue, l.highSellValueRaw);
    estimateCellContent =
      '<div class="' +
      styles.estimateInlineEdit +
      '" data-estimate-id="' +
      escapeAttr(l.id) +
      '">' +
      '<input type="text" inputmode="decimal" pattern="\\d*(?:\\.\\d{0,2})?" class="' +
      styles.estimateInput +
      '" data-field="low" placeholder="Low" aria-label="Low estimate" autocomplete="off" value="' +
      escapeAttr(editLowValue) +
      '" />' +
      '<span class="' +
      styles.estimateSeparator +
      '">-</span>' +
      '<input type="text" inputmode="decimal" pattern="\\d*(?:\\.\\d{0,2})?" class="' +
      styles.estimateInput +
      '" data-field="high" placeholder="High" aria-label="High estimate" autocomplete="off" value="' +
      escapeAttr(editHighValue) +
      '" />' +
      '<button type="button" class="estimate-apply-button primary ' +
      styles.estimateInlineAction +
      '" data-action="apply-estimate" data-id="' +
      escapeAttr(l.id) +
      '">Apply</button>' +
      '<button type="button" class="estimate-cancel-button link ' +
      styles.estimateInlineAction +
      '" data-action="cancel-estimate" data-id="' +
      escapeAttr(l.id) +
      '">Cancel</button>' +
      '</div>';
  } else if (canEditEstimate) {
    let buttonAttrs =
      'type="button" class="' +
      styles.estimateInlineDisplay +
      '" data-action="edit-estimate" data-id="' +
      escapeAttr(l.id) +
      '" aria-label="Edit low and high estimate" title="Click to edit estimate"';
    if (pendingEstimateSave) {
      buttonAttrs += ' disabled';
    }
    let displayWrapper =
      '<div class="' +
      styles.estimateDisplayWrapper +
      '">' +
      '<button ' +
      buttonAttrs +
      '>' +
      lowHighEstimate +
      '</button>';
    if (pendingEstimateSave) {
      displayWrapper +=
        '<div class="' + styles.estimateSaving + '" role="status" aria-live="polite">Saving…</div>';
    }
    displayWrapper += '</div>';
    estimateCellContent = displayWrapper;
  } else {
    estimateCellContent =
      '<div class="' + styles.estimateDisplayWrapper + '">' + lowHighEstimate + '</div>';
  }

  return estimateCellContent;
}
